<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts" generic="T">
import { primeVueTwMerger } from '@/components/primevue'
import { useLocalization } from '@/plugins/localization'
import { type DataTableProps } from 'primevue/datatable'
import { ref, watch, type UnwrapRef } from 'vue'

const props = defineProps<DataTableProps>()
const emit = defineEmits<{
  details: [T]
}>()
const { t } = useLocalization()
const selection = defineModel<T | T[]>('selection')

interface Row {
  data: T
}

const selectedItem = ref<T | T[] | undefined>(selection.value)
const rowSelect = ({ data }: { data?: T }) => {
  if (Array.isArray(selectedItem.value) && props.selectionMode === 'multiple') {
    selectedItem.value = selectedItem.value.concat([data] as any[])
  } else {
    selectedItem.value = data as UnwrapRef<T> | undefined
  }
}
const rowUnselect = ({ data }: { data: T }) => {
  if (Array.isArray(selectedItem.value) && props.selectionMode === 'multiple') {
    let index = -1
    if (typeof data === 'object' && data != null) {
      const keys = Object.keys(data)
      index = selectedItem.value.findIndex((item: any) =>
        // eslint-disable-next-line eqeqeq
        keys.every((k) => item[k] == (data as any)[k]),
      )
    } else {
      index = selectedItem.value.indexOf(data as any)
    }
    selectedItem.value = [
      ...selectedItem.value.slice(0, index),
      ...selectedItem.value.slice(index + 1),
    ]
  }
}
const selectAll = () => {
  if (props.selectionMode === 'multiple' && props.value?.length) {
    selectedItem.value = [...props.value]
  }
}
const unselectAll = () => {
  if (props.selectionMode === 'multiple') {
    selectedItem.value = []
  }
}
watch(selectedItem, () => {
  selection.value = selectedItem.value as any
})
// If the selection is changed outside, then propagate this to the inner ref
watch(selection, () => {
  selectedItem.value = selection.value as any
})
</script>

<template>
  <BaseDataTable
    v-bind="props"
    :pt="{
      bodyRow: 'h-14 group',
      ...props.pt,
    }"
    :ptOptions="{
      mergeProps: primeVueTwMerger,
      ...props.ptOptions,
    }"
    row-hover
    :selectionMode
    :selection="selectedItem"
    removable-sort
    @row-dblclick="(row: Row) => emit('details', row.data)"
    @row-select="rowSelect"
    @row-unselect="rowUnselect"
    @row-select-all="selectAll"
    @row-unselect-all="unselectAll"
  >
    <template #empty>
      <div class="rounded border bg-surface-50 p-4">{{ t('noData') }}</div>
    </template>

    <slot />
  </BaseDataTable>
</template>

<!-- 
  Used to highlight the borders when dragging rows in sort mode
  Source: https://github.com/primefaces/primevue-tailwind/blob/4.2.4/assets/styles/primevue/datatable.css
-->
<style lang="postcss">
.p-datatable-tbody > tr.p-datatable-dragpoint-top > td {
  @apply shadow-[inset_0_2px_0_0_theme(colors.primary)];
}

.p-datatable-tbody > tr.p-datatable-dragpoint-bottom > td {
  @apply shadow-[inset_0_-2px_0_0_theme(colors.primary)];
}
</style>
